import { useDataTypesHooks } from "../../../features/data-types"
import { FieldType, O } from "../../../features/ui"
import { useStateHooks } from "../../../state"
import { TableName } from "../../../tables"
import { MeasureType } from "../../common"
import { useUnitTypeLoaders } from "../loaders/unitTypeLoaders"
import { UnitType, UnitTypeCategory, UnitTypeName } from "../state/types"

export const useUnitTypeHooks = () => {
    const { find } = useStateHooks()
    const { formatValue } = useDataTypesHooks()

    const unitTypeLoaders = useUnitTypeLoaders()

    const describeContent = (
        units: number,
        unitTypeId: string
    ): string => {
        const unitType = find(TableName.UNIT_TYPES, unitTypeId)! as UnitType
        return `${formatValue(units, FieldType.NUMBER)} ${unitType.abbreviation}`
    }

    const getDefaultUnitTypeId = (type: MeasureType): O<string> => {
        const { findUnitTypeByName } = unitTypeLoaders()
        const unitTypeIdsMap = new Map<MeasureType, string>()
            .set(MeasureType.WEIGHT, UnitTypeName.KILOGRAM)    
            .set(MeasureType.THICKNESS, UnitTypeName.MILLIMETER)
            .set(MeasureType.AREA, UnitTypeName.SQUARE_METER)
            .set(MeasureType.DEPTH, UnitTypeName.CENTIMETER)
            .set(MeasureType.DIAMETER, UnitTypeName.CENTIMETER)
            .set(MeasureType.HEIGHT, UnitTypeName.CENTIMETER)
            .set(MeasureType.LENGTH, UnitTypeName.CENTIMETER)
            .set(MeasureType.WIDTH, UnitTypeName.CENTIMETER)
            .set(MeasureType.VOLUME, UnitTypeName.CUBIC_METER)
            
        const unitTypeId = unitTypeIdsMap.get(type) || ''
        return findUnitTypeByName(unitTypeId)?.id
    }

    return {
        describeContent,
        getDefaultUnitTypeId
    }
}

export const unitTypeHelpers = {
    getEquivalence: (unitType_1: UnitType, unitType_2: UnitType): O<number> => {
        const lengthFactors = new Map<string, number>()
            .set(UnitTypeName.MILLIMETER, 1)
            .set(UnitTypeName.CENTIMETER, 10)
            .set(UnitTypeName.METER, 1_000)
        const weightFactors = new Map<string, number>()
            .set(UnitTypeName.MILLIGRAM, 1)
            .set(UnitTypeName.GRAM, 1000)
            .set(UnitTypeName.KILOGRAM, 1_000_000)
            .set(UnitTypeName.TON, 1_000_000_000)
        const areaFactors = new Map<string, number>()
            .set(UnitTypeName.SQUARE_CENTIMETER, 1)
            .set(UnitTypeName.SQUARE_METER, 10_000)
        const volumeFactors = new Map<string, number>()
            .set(UnitTypeName.MILLILITER, 1)
            .set(UnitTypeName.LITER, 1_000)
            .set(UnitTypeName.CUBIC_METER, 1_000_000)
        
        if (unitType_1.category === unitType_2.category) {
            if (unitType_1.category === UnitTypeCategory.LENGTH) {
                const factor_1 = lengthFactors.get(unitType_1.name)!
                const factor_2 = lengthFactors.get(unitType_2.name)!
                return factor_1 / factor_2
            } else if (unitType_1.category === UnitTypeCategory.WEIGHT) {
                const factor_1 = weightFactors.get(unitType_1.name)!
                const factor_2 = weightFactors.get(unitType_2.name)!
                return factor_1 / factor_2
            } else if (unitType_1.category === UnitTypeCategory.AREA) {
                const factor_1 = areaFactors.get(unitType_1.name)!
                const factor_2 = areaFactors.get(unitType_2.name)!
                return factor_1 / factor_2
            } else if (unitType_1.category === UnitTypeCategory.VOLUME) {
                const factor_1 = volumeFactors.get(unitType_1.name)!
                const factor_2 = volumeFactors.get(unitType_2.name)!
                return factor_1 / factor_2
            }
        }
    }
}
